import { registerRuntimeLibraries } from './hostRuntime';

/**
 * Client-side lazy loading of the diagram/animation libraries a custom-component artifact may import.
 *
 * The compiler rewrites e.g. `import { LinePath } from '@visx/shape'` to a shim URL whose served
 * source reads `globalThis.__mutinyHostRuntime.runtimeLibraries['visx-shape']`. That map is populated
 * here, on demand: the loader calls `ensureRuntimeLibrariesRegistered()` only when an artifact (or one
 * of its children) references a non-react runtime library, so pages without diagram components never
 * pull these chunks. The dynamic `import()`s are code-split by the consuming bundler.
 *
 * Slugs MUST match the champaign runtime-library registry
 * (`apps/champaign/src/modules/customComponents/services/runtimeLibraries.ts`). A drift surfaces as a
 * clear "runtime library not registered" error from the shim rather than a silent failure.
 */
export async function loadRuntimeLibraryModules(slugs: readonly string[]): Promise<Record<string, unknown>> {
  const requested = new Set(slugs);
  const modules: Record<string, unknown> = {};
  const tasks: Promise<void>[] = [];

  if (requested.has('visx-shape')) {
    tasks.push(import('@visx/shape').then((mod) => void (modules['visx-shape'] = mod)));
  }
  if (requested.has('visx-curve')) {
    tasks.push(import('@visx/curve').then((mod) => void (modules['visx-curve'] = mod)));
  }
  if (requested.has('visx-group')) {
    tasks.push(import('@visx/group').then((mod) => void (modules['visx-group'] = mod)));
  }
  if (requested.has('visx-scale')) {
    tasks.push(import('@visx/scale').then((mod) => void (modules['visx-scale'] = mod)));
  }
  if (requested.has('motion-react')) {
    tasks.push(import('motion/react').then((mod) => void (modules['motion-react'] = mod)));
  }
  if (requested.has('gsap')) {
    tasks.push(
      Promise.all([
        import('gsap'),
        import('gsap/MorphSVGPlugin'),
        import('gsap/DrawSVGPlugin'),
        import('gsap/MotionPathPlugin'),
      ]).then(([gsapModule, morph, draw, motionPath]) => {
        // Pre-register the diagram-relevant gsap plugins on the shared gsap singleton so author code
        // can use `gsap.to(el, { morphSVG, drawSVG, motionPath })` without calling registerPlugin.
        gsapModule.gsap.registerPlugin(morph.MorphSVGPlugin, draw.DrawSVGPlugin, motionPath.MotionPathPlugin);
        modules.gsap = gsapModule;
      }),
    );
  }
  if (requested.has('gsap-react')) {
    tasks.push(import('@gsap/react').then((mod) => void (modules['gsap-react'] = mod)));
  }

  await Promise.all(tasks);
  return modules;
}

const registeredSlugs = new Set<string>();
let registrationPromise: Promise<void> = Promise.resolve();

/**
 * Loads requested runtime libraries once and registers them on the host runtime. Awaited by the
 * loader before it imports an artifact that references a runtime library, so the shim always finds
 * its module. Requests are serialized so concurrent artifacts don't clobber the shared runtime map.
 */
export function ensureRuntimeLibrariesRegistered(slugs: readonly string[]): Promise<void> {
  const requested = [...new Set(slugs)].filter(
    (slug) => slug !== 'react' && slug !== 'mutiny-state' && !registeredSlugs.has(slug),
  );
  if (requested.length === 0) return Promise.resolve();

  registrationPromise = registrationPromise
    .catch(() => undefined)
    .then(async () => {
      const missing = requested.filter((slug) => !registeredSlugs.has(slug));
      if (missing.length === 0) return;
      const modules = await loadRuntimeLibraryModules(missing);
      registerRuntimeLibraries(modules);
      for (const slug of Object.keys(modules)) registeredSlugs.add(slug);
    });
  return registrationPromise;
}
